import type {
  AdminTokenUsageResponse,
  AdminUserDetail,
  AdminUserUpdateRequest,
  AdminUsersResponse,
  AdminWaitlistRow,
  CreateDemoLinkRequest,
  DemoLink,
  GrantCreditsEveryoneRequest,
  GrantCreditsRequest,
  GrantInvitesRequest,
} from '@eloquor/api-contract';
import { apiFetch } from './client';

export async function listUsers(): Promise<AdminUsersResponse> {
  return apiFetch<AdminUsersResponse>('/admin/users');
}

export async function getUserDetail(userId: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(userId)}`);
}

export async function updateUser(
  userId: string,
  patch: AdminUserUpdateRequest
): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    json: patch,
  });
}

export async function suspendUser(userId: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(userId)}/suspend`, {
    method: 'POST',
  });
}

export async function unsuspendUser(userId: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(userId)}/unsuspend`, {
    method: 'POST',
  });
}

export async function deleteUser(userId: string): Promise<void> {
  await apiFetch(`/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export async function grantCredits(userId: string, req: GrantCreditsRequest): Promise<void> {
  await apiFetch(`/admin/users/${encodeURIComponent(userId)}/credits`, { method: 'POST', json: req });
}

export async function grantInvites(userId: string, req: GrantInvitesRequest): Promise<void> {
  await apiFetch(`/admin/users/${encodeURIComponent(userId)}/invites`, { method: 'POST', json: req });
}

export async function grantCreditsEveryone(req: GrantCreditsEveryoneRequest): Promise<void> {
  await apiFetch('/admin/credits/everyone', { method: 'POST', json: req });
}

export async function grantInvitesEveryone(req: GrantInvitesRequest): Promise<void> {
  await apiFetch('/admin/invites/everyone', { method: 'POST', json: req });
}

export async function listWaitlist(): Promise<{ rows: AdminWaitlistRow[] }> {
  return apiFetch<{ rows: AdminWaitlistRow[] }>('/admin/waitlist');
}

export async function acceptWaitlist(id: string): Promise<{ code: string }> {
  return apiFetch<{ code: string }>(`/admin/waitlist/${encodeURIComponent(id)}/accept`, {
    method: 'POST',
  });
}

export async function listDemoLinks(): Promise<{ links: DemoLink[] }> {
  return apiFetch<{ links: DemoLink[] }>('/admin/demo-links');
}

export async function createDemoLink(req: CreateDemoLinkRequest): Promise<{ link: DemoLink }> {
  return apiFetch<{ link: DemoLink }>('/admin/demo-links', { method: 'POST', json: req });
}

export async function revokeDemoLink(id: string): Promise<void> {
  await apiFetch(`/admin/demo-links/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function metricsTokens(range: '24h' | '7d'): Promise<AdminTokenUsageResponse> {
  return apiFetch<AdminTokenUsageResponse>(`/admin/metrics/tokens?range=${range}`);
}
